import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Controller,
  get,
  useFieldArray,
  useFormState,
  useWatch,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { resolveFactors, trimDecimal, type Unit, type UnitRow } from './model';

type WithUnits = FieldValues & { units: UnitRow[] };

/**
 * Alternate units (box, carton, dozen…) with how many base units, or how many of another pack,
 * each holds ("1 carton = 50 box", "1 box = 5 pc"). Works in any form that has `units[]`.
 */
export function UnitRowsEditor<T extends WithUnits>({
  control,
  units,
  base,
  disabled,
}: {
  control: Control<T>;
  units: Unit[];
  base: Unit | undefined;
  disabled?: boolean;
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'units' as ArrayPath<T>,
  });
  const rows = (useWatch({ control, name: 'units' as Path<T> }) ?? []) as UnitRow[];
  const { errors } = useFormState({ control });
  const err = (i: number, f: keyof UnitRow) =>
    (get(errors, `units.${String(i)}.${f}`) as { message?: string } | undefined)?.message;
  const n = (i: number, f: keyof UnitRow) => `units.${String(i)}.${f}` as Path<T>;
  const choices = units.filter((u) => u.isActive && u.id !== base?.id);
  const totals = resolveFactors(rows, base?.id);
  const sym = (id: string) => units.find((u) => u.id === id)?.symbol ?? '';

  return (
    <Stack spacing={1.5}>
      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No pack sizes yet. Add one if you buy or sell this in boxes, cartons or dozens.
        </Typography>
      )}
      {fields.map((field, i) => {
        const row = rows[i];
        const unit = units.find((u) => u.id === row?.unitId);
        return (
          <Box
            key={field.id}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              display: 'grid',
              gap: 1.5,
              alignItems: 'start',
              gridTemplateColumns: { xs: '1fr 1fr auto', md: '170px 120px 150px 1fr auto' },
            }}
          >
            <Controller
              control={control}
              name={n(i, 'unitId')}
              render={({ field: f }) => (
                <TextField
                  select
                  label="Unit"
                  value={f.value}
                  onChange={f.onChange}
                  onBlur={f.onBlur}
                  disabled={disabled}
                  error={!!err(i, 'unitId')}
                  helperText={err(i, 'unitId')}
                >
                  {units
                    .filter((u) => u.id !== base?.id && (u.isActive || u.id === f.value))
                    .map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name} ({u.symbol})
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name={n(i, 'factor')}
              render={({ field: f }) => (
                <TextField
                  label="Holds"
                  value={f.value}
                  onChange={f.onChange}
                  onBlur={f.onBlur}
                  disabled={disabled}
                  error={!!err(i, 'factor')}
                  helperText={
                    err(i, 'factor') ??
                    (unit && row && totals[i] !== null
                      ? `1 ${unit.symbol} = ${
                          row.ofUnitId && row.ofUnitId !== base?.id
                            ? `${trimDecimal(row.factor)} ${sym(row.ofUnitId)} = `
                            : ''
                        }${String(totals[i])} ${base?.symbol ?? ''}`
                      : ' ')
                  }
                  slotProps={{ htmlInput: { inputMode: 'decimal', className: 'tabular' } }}
                  sx={{ gridColumn: { xs: 'auto', md: 'auto' } }}
                />
              )}
            />
            <Controller
              control={control}
              name={n(i, 'ofUnitId')}
              render={({ field: f }) => (
                <TextField
                  select
                  label="Of"
                  value={f.value || base?.id || ''}
                  onChange={(e) => f.onChange(e.target.value === base?.id ? '' : e.target.value)}
                  onBlur={f.onBlur}
                  disabled={disabled}
                  error={!!err(i, 'ofUnitId')}
                  helperText={err(i, 'ofUnitId') ?? ' '}
                  sx={{ gridColumn: { xs: '1 / 3', md: 'auto' }, gridRow: { xs: 2, md: 'auto' } }}
                >
                  {base && (
                    <MenuItem value={base.id}>
                      {base.name} ({base.symbol})
                    </MenuItem>
                  )}
                  {rows
                    .filter((o, j) => j !== i && o.unitId)
                    .map((o) => (
                      <MenuItem key={o.unitId} value={o.unitId}>
                        {units.find((u) => u.id === o.unitId)?.name ?? ''} ({sym(o.unitId)})
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
            <Stack
              direction="row"
              sx={{
                gridColumn: { xs: '1 / -1', md: 'auto' },
                gridRow: { xs: 3, md: 'auto' },
                flexWrap: 'wrap',
                alignItems: 'center',
                minHeight: { md: 56 },
              }}
            >
              {(['isDefaultPurchase', 'isDefaultSale'] as const).map((key) => (
                <Controller
                  key={key}
                  control={control}
                  name={n(i, key)}
                  render={({ field: f }) => (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={f.value}
                            onChange={(e) => f.onChange(e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label={
                          key === 'isDefaultPurchase' ? 'Default for buying' : 'Default for selling'
                        }
                      />
                      {err(i, key) && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'block', mt: -0.5, ml: 4 }}
                        >
                          {err(i, key)}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              ))}
            </Stack>
            <Tooltip title="Remove">
              <span>
                <IconButton
                  aria-label="Remove unit"
                  onClick={() => remove(i)}
                  disabled={disabled}
                  sx={{ mt: 1 }}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
      <Box>
        <Button
          startIcon={<AddRounded />}
          disabled={disabled || choices.length === 0 || fields.length >= 20}
          onClick={() =>
            append({
              unitId: choices.find((u) => !rows.some((r) => r.unitId === u.id))?.id ?? '',
              factor: '',
              ofUnitId: '',
              isDefaultPurchase: false,
              isDefaultSale: false,
            } as FieldArray<T, ArrayPath<T>>)
          }
        >
          Add pack size
        </Button>
      </Box>
    </Stack>
  );
}
